/**
 * Headless runtime for the awareness layers and deep-space data flows. Mounted
 * once by the Shell; renders nothing. Each layer is an effect keyed on the
 * loader store's flags, the network and the dataset: the data side polls the
 * feeds into the awareness store, the globe side pushes that state to the
 * engine and re-applies it whenever the engine appears or the globe clears its
 * entities on a dataset swap (engineEpoch, see awareness/engineBridge.ts).
 */
import { useEffect, useRef, useState } from 'react';
import { clockNow } from '@/state/store';
import { toast } from '@/components/ui/toast';
import {
  computeTelemetry,
  currentPhase,
  loadArtemisImages,
  loadTimeline,
  phaseAntennaVisibility,
  startArtemisBlog,
  startArtemisSpaceWeather,
  TELEMETRY_TICK_MS,
} from './awareness/artemis';
import { startAurora } from './awareness/aurora';
import { startConjunctions } from './awareness/conjunctions';
import { dsnContactPairs, simTimeDiffers, startDsn } from './awareness/dsn';
import { startEngineWatcher, withEngine, withEngineAsync } from './awareness/engineBridge';
import { EphemerisTrack } from './awareness/ephemeris';
import { startGpsJam } from './awareness/gpsjam';
import { horizonsBody, loadHorizonsBody } from './awareness/horizons';
import { launchPads, startLaunches } from './awareness/launches';
import {
  clearImpactCache,
  isNearPresent,
  LIVE_WEATHER_TOGGLE,
  startLiveWeather,
} from './awareness/liveWeather';
import { isLunarMission, loadLunarEphemeris } from './awareness/lunar';
import { loadOverlay, overlayDef } from './awareness/overlays';
import { PULSE_MS, startSatNogs } from './awareness/satnogs';
import { startSpaceWeather } from './awareness/spaceWeather';
import { useAwareness, type OrionTelemetry } from './awareness/store';
import { startSunOutageMonitor } from './awareness/sunOutage';
import { applyMasks, startTerrainMasks } from './awareness/terrainMasks';
import { errorMessage } from './awareness/util';
import { isDeepSpaceNetwork, useLoader } from './store';

const CLOCK_CHECK_MS = 1000;
/** Stable empty list for the slim profile (no overlays or deep-space bodies). */
const NO_KEYS: string[] = [];
/** Lunar chain generation: a network switch bumps it so late results are discarded. */
let lunarGeneration = 0;

export function AwarenessRuntime() {
  const network = useLoader((s) => s.network);
  const dataset = useLoader((s) => s.dataset);
  const datasetStatus = useLoader((s) => s.datasetStatus);
  const slim = useLoader((s) => s.profile) === 'noaa-core';
  const activeAntennaIds = useLoader((s) => s.activeAntennaIds);
  const visibleAntennaIds = useLoader((s) => s.visibleAntennaIds);
  const auroraEnabled = useLoader((s) => s.auroraEnabled) && !slim;
  const conjunctionsEnabled = useLoader((s) => s.conjunctionsEnabled) && !slim;
  const spaceWeatherEnabled = useLoader((s) => s.spaceWeatherEnabled) && !slim;
  const launchesEnabled = useLoader((s) => s.launchesEnabled) && !slim;
  const satnogsEnabled = useLoader((s) => s.satnogsEnabled) && !slim;
  const gpsJamEnabled = useLoader((s) => s.gpsJamEnabled) && !slim;
  const overlayKeysRaw = useLoader((s) => s.overlays);
  const horizonsKeysRaw = useLoader((s) => s.horizonsBodies);
  const overlayKeys = slim ? NO_KEYS : overlayKeysRaw;
  const horizonsKeys = slim ? NO_KEYS : horizonsKeysRaw;
  const liveNwsToggle = useLoader((s) => s.toggles[LIVE_WEATHER_TOGGLE] ?? true) && !slim;

  const engineMounted = useAwareness((s) => s.engineMounted);
  const engineEpoch = useAwareness((s) => s.engineEpoch);
  const liveMode = useAwareness((s) => s.liveMode);
  const launches = useAwareness((s) => s.launches);
  const auroraDataUrl = useAwareness((s) => s.auroraDataUrl);
  const gpsJam = useAwareness((s) => s.gpsJam);
  const horizons = useAwareness((s) => s.horizons);
  const overlays = useAwareness((s) => s.overlays);
  const lunar = useAwareness((s) => s.lunar);
  const dsnData = useAwareness((s) => s.dsn.data);
  const timeline = useAwareness((s) => s.artemis.timeline);
  const currentPhaseId = useAwareness((s) => s.artemis.currentPhaseId);

  /** Sim clock within 1 h of real time (DSN live contact lines). */
  const [dsnRealTime, setDsnRealTime] = useState(() => !simTimeDiffers(clockNow(), Date.now()));

  const isArtemis = network === 'artemis';
  const deepSpace = isDeepSpaceNetwork(network);
  const missions = dataset?.missions ?? null;

  // ---------------------------------------------------------------- engine

  // Presence of the globe engine, polled every second.
  useEffect(() => startEngineWatcher(), []);

  // ----------------------------------------------------------------- clock

  // Live-mode detection (24 h) and the DSN real-time rule (1 h), checked every second.
  useEffect(() => {
    const check = () => {
      const sim = clockNow();
      const real = Date.now();
      const near = isNearPresent(sim, real);
      const store = useAwareness.getState();
      if (store.liveMode !== near) store.setLiveMode(near);
      setDsnRealTime(!simTimeDiffers(sim, real));
    };
    check();
    const id = setInterval(check, CLOCK_CHECK_MS);
    return () => clearInterval(id);
  }, []);

  // ------------------------------------------------------ awareness feeds

  useEffect(() => (spaceWeatherEnabled ? startSpaceWeather() : undefined), [spaceWeatherEnabled]);
  useEffect(() => (launchesEnabled ? startLaunches() : undefined), [launchesEnabled]);
  useEffect(() => (auroraEnabled ? startAurora() : undefined), [auroraEnabled]);
  useEffect(() => (conjunctionsEnabled ? startConjunctions() : undefined), [conjunctionsEnabled]);
  useEffect(() => (gpsJamEnabled ? startGpsJam() : undefined), [gpsJamEnabled]);
  useEffect(() => {
    if (!satnogsEnabled) return;
    return startSatNogs((pulse) =>
      withEngine('pulseStation', (engine) =>
        engine.pulseStation(String(pulse.observationId), pulse.lat, pulse.lon, PULSE_MS),
      ),
    );
  }, [satnogsEnabled]);

  // Launch pads: pushed while enabled, cleared (null) when the list goes away.
  useEffect(() => {
    if (!engineMounted) return;
    const pads = launchesEnabled && launches ? launchPads(launches.launches) : null;
    withEngine('setLaunchPads', (engine) => engine.setLaunchPads(pads));
  }, [engineMounted, engineEpoch, launchesEnabled, launches]);

  // Aurora imagery survives the globe's clearAll, so it re-applies on mount and image changes only.
  useEffect(() => {
    if (!engineMounted) return;
    withEngine('setAuroraImage', (engine) => engine.setAuroraImage(auroraDataUrl));
  }, [engineMounted, auroraDataUrl]);

  useEffect(() => {
    if (!engineMounted) return;
    withEngine('setGpsJamPolygons', (engine) => engine.setGpsJamPolygons(gpsJam));
  }, [engineMounted, engineEpoch, gpsJam]);

  // -------------------------------------------------------- Horizons bodies

  const horizonsInflight = useRef(new Set<string>());
  useEffect(() => {
    const store = useAwareness.getState();
    for (const key of horizonsKeys) {
      if (horizons[key] || horizonsInflight.current.has(key)) continue;
      horizonsInflight.current.add(key);
      loadHorizonsBody(key)
        .catch((err: unknown) => {
          const label = horizonsBody(key)?.label ?? key;
          console.warn(`[horizons] ${label} unavailable:`, err);
          toast('warn', `${label} is unavailable from JPL Horizons: ${errorMessage(err)}`);
          if (useLoader.getState().horizonsBodies.includes(key)) useLoader.getState().toggleHorizonsBody(key);
        })
        .finally(() => horizonsInflight.current.delete(key));
    }
    for (const key of Object.keys(horizons)) {
      if (horizonsKeys.includes(key)) continue;
      store.setHorizonsBody(key, null);
      withEngine('removeHorizonsBody', (engine) => engine.removeHorizonsBody(key));
    }
  }, [horizonsKeys, horizons]);

  const appliedHorizons = useRef(new Map<string, { epoch: number; body: unknown }>());
  useEffect(() => {
    if (!engineMounted) return;
    for (const [key, body] of Object.entries(horizons)) {
      const applied = appliedHorizons.current.get(key);
      if (applied && applied.epoch === engineEpoch && applied.body === body) continue;
      appliedHorizons.current.set(key, { epoch: engineEpoch, body });
      withEngine('setHorizonsBody', (engine) => engine.setHorizonsBody(key, body.label, body.samples));
    }
    for (const key of appliedHorizons.current.keys()) if (!horizons[key]) appliedHorizons.current.delete(key);
  }, [engineMounted, engineEpoch, horizons]);

  // ------------------------------------------------------------- overlays

  const overlayInflight = useRef(new Map<string, AbortController>());
  useEffect(() => {
    const store = useAwareness.getState();
    for (const key of overlayKeys) {
      if (overlays[key] || overlayInflight.current.has(key)) continue;
      const controller = new AbortController();
      overlayInflight.current.set(key, controller);
      loadOverlay(key, { signal: controller.signal })
        .then((state) => {
          if (controller.signal.aborted) return;
          toast('success', `${state.label} overlay loaded: ${state.sats.length} satellites.`);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          const label = overlayDef(key)?.label ?? key;
          console.warn(`[overlays] ${label} failed:`, err);
          toast('error', `${label} overlay failed: ${errorMessage(err)}`);
          if (useLoader.getState().overlays.includes(key)) useLoader.getState().toggleOverlay(key);
        })
        .finally(() => overlayInflight.current.delete(key));
    }
    for (const [key, controller] of overlayInflight.current) {
      if (!overlayKeys.includes(key)) {
        controller.abort();
        overlayInflight.current.delete(key);
      }
    }
    for (const key of Object.keys(overlays)) {
      if (overlayKeys.includes(key)) continue;
      store.setOverlay(key, null);
      withEngineAsync('removeOverlay', (engine) => engine.removeOverlay(key));
    }
  }, [overlayKeys, overlays]);

  const appliedOverlays = useRef(new Map<string, { epoch: number; state: unknown }>());
  useEffect(() => {
    if (!engineMounted) return;
    for (const [key, state] of Object.entries(overlays)) {
      const applied = appliedOverlays.current.get(key);
      if (applied && applied.epoch === engineEpoch && applied.state === state) continue;
      appliedOverlays.current.set(key, { epoch: engineEpoch, state });
      withEngineAsync('addOverlay', (engine) => engine.addOverlay(key, state.sats, state.color));
    }
    for (const key of appliedOverlays.current.keys()) if (!overlays[key]) appliedOverlays.current.delete(key);
  }, [engineMounted, engineEpoch, overlays]);

  // --------------------------------------------------------------- lunar

  // The ephemeris chain per mission list; the generation discards late results after a switch.
  useEffect(() => {
    const mission = missions?.find(isLunarMission) ?? null;
    const store = useAwareness.getState();
    if (!mission) {
      if (store.lunar) store.setLunar(null);
      return;
    }
    const generation = ++lunarGeneration;
    loadLunarEphemeris(mission)
      .then((result) => {
        if (generation !== lunarGeneration) return;
        useAwareness.getState().setLunar({ missionId: mission.id, ...result });
      })
      .catch((err: unknown) => {
        if (generation !== lunarGeneration) return;
        console.error(`[lunar] no ephemeris for ${mission.id}:`, err);
        toast(
          'warn',
          `Could not load orbit data: ${mission.name || mission.id} will not appear on the globe.`,
        );
        useAwareness.getState().setLunar(null);
      });
    return () => {
      lunarGeneration++;
      useAwareness.getState().setLunar(null);
    };
  }, [missions]);

  useEffect(() => {
    if (!engineMounted) return;
    const mission = lunar && missions ? (missions.find((m) => m.id === lunar.missionId) ?? null) : null;
    withEngine('setLunarMission', (engine) => {
      if (lunar && mission) {
        engine.setLunarMission(mission, lunar.samples, lunar.source);
        engine.setMoonMarker(lunar.moonSamples);
        if (isArtemis) engine.flyToMissionOverview();
      } else {
        engine.clearLunarMission();
      }
      engine.setSpaceEnvironment(Boolean(lunar) || isArtemis);
    });
  }, [engineMounted, engineEpoch, lunar, missions, isArtemis]);

  // ----------------------------------------------------------------- DSN

  useEffect(() => (deepSpace ? startDsn() : undefined), [deepSpace]);

  // Live DSN contact pairs, only within 1 h of real time; LoaderGlobe merges them into the drawn contacts.
  useEffect(() => {
    const pairs =
      deepSpace && dataset && dsnRealTime
        ? dsnContactPairs(
            dsnData,
            new Set(dataset.missions.map((m) => m.id)),
            new Set(dataset.antennas.map((a) => a.id)),
          )
        : [];
    const store = useAwareness.getState();
    if (pairs.length === 0 && store.dsnContactPairs.length === 0) return;
    store.setDsnContactPairs(pairs);
  }, [deepSpace, dataset, dsnData, dsnRealTime]);

  // --------------------------------------------------------------- Artemis

  useEffect(() => {
    if (!isArtemis) return;
    const stopBlog = startArtemisBlog();
    const stopWeather = startArtemisSpaceWeather();
    void loadArtemisImages();
    void loadTimeline();
    return () => {
      stopBlog();
      stopWeather();
      useAwareness.getState().resetArtemis();
    };
  }, [isArtemis]);

  // Phase from the sim clock and Orion telemetry at 4 Hz from the ephemeris samples.
  useEffect(() => {
    if (!isArtemis || !timeline) return;
    const phases = timeline.phases;
    const launchMs = new Date(timeline.launchDate || phases[0]?.start || NaN).getTime();
    const track = lunar ? new EphemerisTrack(lunar.samples) : null;
    const moonTrack = lunar?.moonSamples ? new EphemerisTrack(lunar.moonSamples) : null;
    const tick = () => {
      const t = clockNow();
      const store = useAwareness.getState();
      const phaseId = currentPhase(phases, t)?.id ?? null;
      const telemetry: OrionTelemetry | null =
        track && Number.isFinite(launchMs) ? computeTelemetry(track, moonTrack, t, launchMs, phases) : null;
      const patch: { currentPhaseId?: string | null; telemetry?: OrionTelemetry | null } = {};
      if (phaseId !== store.artemis.currentPhaseId) patch.currentPhaseId = phaseId;
      if (telemetry || store.artemis.telemetry) patch.telemetry = telemetry;
      if (Object.keys(patch).length > 0) store.setArtemis(patch);
    };
    tick();
    const id = setInterval(tick, TELEMETRY_TICK_MS);
    return () => clearInterval(id);
  }, [isArtemis, timeline, lunar]);

  // Antennas with enabledPhases are shown only in those phases (within the user's own filters).
  useEffect(() => {
    if (!engineMounted || !isArtemis || !dataset || !currentPhaseId) return;
    const active = new Set(activeAntennaIds);
    const visible = visibleAntennaIds ? new Set(visibleAntennaIds) : null;
    const rules = phaseAntennaVisibility(dataset.antennas, currentPhaseId);
    if (rules.length === 0) return;
    withEngine('setAntennaVisible (phase)', (engine) => {
      for (const rule of rules) {
        engine.setAntennaVisible(
          rule.id,
          rule.visible && active.has(rule.id) && (!visible || visible.has(rule.id)),
        );
      }
    });
  }, [engineMounted, engineEpoch, isArtemis, dataset, currentPhaseId, activeAntennaIds, visibleAntennaIds]);

  // ------------------------------------------------------- sun outage

  useEffect(() => {
    if (datasetStatus !== 'ready' || !dataset) return;
    const stop = startSunOutageMonitor(
      () => {
        const st = useLoader.getState();
        const ds = st.dataset;
        if (!ds) return { antennas: [], missions: [] };
        const activeA = new Set(st.activeAntennaIds);
        const activeM = new Set(st.activeMissionIds);
        return {
          antennas: ds.antennas.filter((a) => activeA.has(a.id)),
          missions: ds.missions.filter((m) => activeM.has(m.id)),
        };
      },
      (events, badge, nowMs) => {
        useAwareness.getState().setSunOutageUpcoming(events, nowMs);
        useLoader.getState().setSunOutage(badge);
      },
    );
    return () => {
      stop();
      useAwareness.getState().setSunOutageUpcoming([], null);
      useLoader.getState().setSunOutage(null);
    };
  }, [datasetStatus, dataset]);

  // ---------------------------------------------------------- terrain masks

  // Once per dataset load (not after patches): masks for up to 60 antennas without one.
  useEffect(() => {
    if (datasetStatus !== 'ready') return;
    const ds = useLoader.getState().dataset;
    if (!ds) return;
    return startTerrainMasks(ds, (masks) => {
      const current = useLoader.getState().dataset;
      if (!current || current.network !== ds.network) return;
      useLoader.getState().patchDataset({ antennas: applyMasks(current.antennas, masks) });
    });
  }, [datasetStatus, network]);

  // ---------------------------------------------------------- live weather

  useEffect(() => {
    clearImpactCache();
  }, [network]);

  useEffect(() => {
    if (!liveMode || !liveNwsToggle || datasetStatus !== 'ready') return;
    return startLiveWeather(() => {
      const st = useLoader.getState();
      const ds = st.dataset;
      if (!ds) return [];
      const active = new Set(st.activeAntennaIds);
      return ds.antennas.filter((a) => active.has(a.id));
    });
  }, [liveMode, liveNwsToggle, datasetStatus, network]);

  return null;
}
